import { brandAliasLookupState, brandRequestLookupState, joinAliasKeys, sharedNameplateTableState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import {
    brandPickStands,
    catalogRequestIdFor,
    followLiveBrand,
    routeSharedNameplate,
    type SharedNameplateTable,
} from '@rock/brands';
import { type JoinBrand, joinBrandOf, joinCandidateOf, requestedJoinBrand } from '@rock/models';
import type { Brand, BrandId, BrandRequest, BrandRequestId, Equipment, EquipmentId, Model } from '@rock/types';
import { normalizeBrandName } from '@rock/util';
import { useMemo } from 'react';

/** The brand an id names today: merges followed, junk nothing — through the loaded catalog. */
export function liveBrandOf(brandId: BrandId | undefined, brandsById: Map<BrandId, Brand>): Brand | undefined {
    return brandId ? followLiveBrand(brandId, (id) => brandsById.get(id)) : undefined;
}

/** A model's brand as the join judges it: its live brand, else the brand request it waits on. */
export function modelJoinBrand(model: Model, brandsById: Map<BrandId, Brand>): JoinBrand | undefined {
    return joinCandidateOf(model, liveBrandOf(model.brandId, brandsById)).brand;
}

/** A unit's label brand as the join reads it, and the name an admin reads beside it. */
export interface UnitJoinBrand {
    join: JoinBrand | undefined;
    label: string | undefined;
}

/**
 * The brand on each unit's own label, as the join read it (`observedJoinBrand` on the server): the owner's
 * typeahead pick while it stands (`brandPickStands`), else what the text resolves to through `brand-aliases`,
 * else a shared nameplate the unit's space routes, else the brand request a name the catalog lacks waits on.
 */
export function useUnitBrands(units: Equipment[], brandsById: Map<BrandId, Brand>): Map<EquipmentId, UnitJoinBrand> {
    const keys = useMemo(
        () => joinAliasKeys(units.map((unit) => normalizeBrandName(unit.fields?.brand?.trim() ?? '').key)),
        [units],
    );
    const aliases = useAsyncAtomValue(
        useMemo(() => brandAliasLookupState(keys), [keys]),
        new Map(),
    );
    const nameplates = useAsyncAtomValue(sharedNameplateTableState, []);
    const requestIds = useMemo(
        () => joinAliasKeys(units.map((unit) => catalogRequestIdFor(unit.fields?.brand?.trim(), nameplates) ?? '')),
        [units, nameplates],
    );
    const requests = useAsyncAtomValue(
        useMemo(() => brandRequestLookupState(requestIds), [requestIds]),
        new Map(),
    );
    return useMemo(
        () =>
            new Map(
                units.map((unit): [EquipmentId, UnitJoinBrand] => {
                    const text = unit.fields?.brand?.trim();
                    const ref = unit.fields?.brandRef;
                    const picked = ref ? liveBrandOf(ref.brandId, brandsById) : undefined;
                    const key = text ? normalizeBrandName(text).key : '';
                    const brand =
                        (picked && ref && brandPickStands(picked, ref, text, nameplates) ? picked : undefined) ??
                        liveBrandOf(key ? aliases.get(key)?.brandId : undefined, brandsById) ??
                        (text
                            ? liveBrandOf(routeSharedNameplate(text, unit.spaceId, nameplates), brandsById)
                            : undefined);
                    if (brand) return [unit.id, { join: joinBrandOf(brand), label: brand.canonicalName }];
                    return [unit.id, { join: requestJoinBrand(text, nameplates, requests, brandsById), label: text }];
                }),
            ),
        [units, brandsById, aliases, nameplates, requests],
    );
}

/** A name the catalog lacks, as the server's join reads it: its pending request, the brand it resolved to, or nothing. */
function requestJoinBrand(
    text: string | undefined,
    nameplates: SharedNameplateTable,
    requests: Map<BrandRequestId, BrandRequest | undefined>,
    brandsById: Map<BrandId, Brand>,
): JoinBrand | undefined {
    const requestId = catalogRequestIdFor(text, nameplates);
    if (!requestId) return undefined;
    const request = requests.get(requestId);
    if (request?.status !== 'resolved') return requestedJoinBrand(requestId);
    return joinBrandOf(liveBrandOf(request.resolution?.brandId, brandsById));
}
